import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { fetchPortalData } from '@/api/portals'
import { PortalShell } from '@/layouts/PortalShell'
import { ErrorState, PageSkeleton } from '@/components/ui/States'
import { getApiErrorMessage } from '@/lib/apiClient'

export default function RoleDashboardPage({
  title,
  apiPath,
  links = [],
}: {
  title: string
  apiPath: string
  links?: Array<{ label: string; to: string }>
}) {
  const q = useQuery({
    queryKey: ['portal', apiPath],
    queryFn: () => fetchPortalData(apiPath),
  })

  if (q.isLoading) {
    return (
      <PortalShell title={title}>
        <PageSkeleton />
      </PortalShell>
    )
  }
  if (q.isError) {
    return (
      <PortalShell title={title}>
        <div className="p-6">
          <ErrorState message={getApiErrorMessage(q.error)} onRetry={() => void q.refetch()} />
        </div>
      </PortalShell>
    )
  }

  const data = q.data || {}
  const stats = (data.stats as Record<string, unknown>) || {}
  const statEntries = Object.entries(stats)

  return (
    <PortalShell title={title}>
      <div className="mx-auto max-w-6xl space-y-5 p-6">
        {links.length ? (
          <div className="flex flex-wrap gap-2">
            {links.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-bold text-slate-700 shadow-sm"
              >
                {l.label}
              </Link>
            ))}
          </div>
        ) : null}

        {statEntries.length ? (
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            {statEntries.map(([k, v]) => (
              <div key={k} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500">
                  {k.replace(/_/g, ' ')}
                </div>
                <div className="mt-1 text-2xl font-black text-slate-900">{String(v)}</div>
              </div>
            ))}
          </div>
        ) : null}

        <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <div className="mb-2 text-sm font-bold text-slate-800">Live portal data</div>
          <pre className="max-h-[480px] overflow-auto rounded-lg bg-slate-50 p-3 text-xs text-slate-700">
            {JSON.stringify(data, null, 2)}
          </pre>
        </div>
      </div>
    </PortalShell>
  )
}
